import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { DropdownOption, GroupSelection, SearchDropdownInput } from '@aslanpay/portal-common-lib';

import { getPointsValue } from 'app/utils/points';
import { formatCurrency } from 'app/utils/strings';

import { SCHEDULE_MOMENTS } from 'app/utils/campaigns/schedule';
import { RecurrenceFormState } from 'app/utils/campaigns/recurrence';

import AdvancedFields, { AdvancedFormState } from '../AdvancedFields';
import RecurrenceFields from '../RecurrenceFields';

import FieldHelp from './FieldHelp';
import ReachSummary from './ReachSummary';
import {
  CreateCampaignField,
  CreateCampaignFormState,
  SCHEDULE_STEP_MINUTES,
} from './useCreateCampaignForm';

interface CreateCampaignFormProps {
  form: CreateCampaignFormState;
  setField: <K extends keyof CreateCampaignFormState>(
    key: K,
    value: CreateCampaignFormState[K]
  ) => void;
  prides: { id: string; name: string }[];
  /** Employees the nominee shortlist may be built from, narrowed by what has been searched for. */
  candidates: DropdownOption[];
  onSearchCandidates: (query: string) => void;
  fansOut: boolean;
  /** The dates the repeat produces, shown under it. */
  forecast: React.ReactNode;
  errorFor: (field: CreateCampaignField) => string | undefined;
  isSaving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * Every time of day the schedule allows, as "HH:mm". A dropdown rather than a time input because the
 * browser's own picker offers every minute whatever step it is given.
 */
const TIME_OPTIONS = Array.from({ length: (24 * 60) / SCHEDULE_STEP_MINUTES }, (_, index) => {
  const minutesFromMidnight = index * SCHEDULE_STEP_MINUTES;
  const hours = String(Math.floor(minutesFromMidnight / 60)).padStart(2, '0');
  const minutes = String(minutesFromMidnight % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
});

const OVERVIEW =
  'A campaign is an award people are nominated for. Colleagues put someone forward while nominations are open; once they close, the people running the campaign choose a winner from those nominations; on the announcement date the winner is revealed and their points are paid.';

/** GROUP scope is a fourth answer to the same question, so it sits with the other three. */
const ONE_PRIDE = 'ONE_PRIDE';

type ReachOption = GroupSelection | typeof ONE_PRIDE;

const FAN_OUT_OPTIONS: { value: ReachOption; label: string; help: string }[] = [
  {
    value: 'NONE',
    label: 'One winner for the whole company',
    help: 'Everybody is in the same contest and one person wins it. No pride is involved, so nominating cannot be limited to one either.',
  },
  {
    value: 'ALL',
    label: 'A winner in every pride',
    help: 'The campaign runs separately inside each pride, so each one gets its own winner. The list of prides is worked out again every time the campaign runs, so a pride created later joins on its own and an archived one drops out.',
  },
  {
    value: 'SELECTED',
    label: 'A winner in the prides you choose',
    help: 'The campaign runs separately inside each pride you tick, so each one gets its own winner. The list is fixed — a pride created later will not join until someone edits the campaign.',
  },
  {
    value: ONE_PRIDE,
    label: 'One pride only',
    help: 'The award belongs to a single pride outright rather than being a company award that reached it. One winner, in that pride.',
  },
];

const dateOf = (value: string) => value.split('T')[0] ?? '';

const timeOf = (value: string) => value.split('T')[1] ?? '';

/** Half a schedule entry is kept rather than dropped, so the other half is not lost while it is filled in. */
const join = (date: string, time: string) => (date || time ? `${date}T${time}` : '');

function Section({
  title,
  help,
  testId,
  children,
}: {
  title: string;
  help: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <Flex
      bgColor="brand.white"
      borderRadius="10px"
      border="1px solid"
      borderColor="brand.lightGray1"
      w="full"
      p="20px"
      flexDir="column"
      gap="16px"
    >
      <Flex align="center" gap="6px">
        <Text as="h5" textStyle="h5SemiBold" color="brand.darkPurple">
          {title}
        </Text>
        <FieldHelp subject={title} help={help} testId={testId} />
      </Flex>
      {children}
    </Flex>
  );
}

function LabelWithHelp({ label, help, testId }: { label: string; help: string; testId: string }) {
  return (
    <FormLabel display="flex" alignItems="center" gap="6px" mb="8px">
      {label}
      <FieldHelp subject={label} help={help} testId={testId} />
    </FormLabel>
  );
}

function CheckboxField({
  isChecked,
  onChange,
  testId,
  helpTestId,
  label,
  help,
  isDisabled = false,
}: {
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
  helpTestId: string;
  label: string;
  help: string;
  isDisabled?: boolean;
}) {
  return (
    <Flex align="center" gap="6px">
      <Checkbox
        isChecked={isChecked}
        isDisabled={isDisabled}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      >
        {label}
      </Checkbox>
      <FieldHelp subject={label} help={help} testId={helpTestId} />
    </Flex>
  );
}

function CreateCampaignForm({
  form,
  setField,
  prides,
  candidates,
  onSearchCandidates,
  fansOut,
  forecast,
  errorFor,
  isSaving,
  onSubmit,
  onCancel,
}: CreateCampaignFormProps) {
  // How many prides the campaign reaches as it stands, which is what the award is multiplied by.
  const prideCountOf = () => {
    if (form.scope === 'GROUP' || form.groupSelection === 'NONE') return 1;
    if (form.groupSelection === 'SELECTED') return form.groupIds.length;

    return prides.length;
  };

  const prideCount = prideCountOf();

  const award = Number(form.awardPoints);
  const awardInPounds =
    form.awardPoints !== '' && Number.isFinite(award) && award >= 0
      ? getPointsValue(award)
      : undefined;

  const eligibilityHelp = (who: string) =>
    fansOut
      ? `Anyone in the company lets ${who} take part in any pride's award. Only people in that pride keeps each award to its own members.`
      : 'This award has one winner for the whole company, so there is no pride to limit it to.';

  return (
    <Flex flexDir="column" gap="20px">
      <Flex align="center" gap="6px">
        <Text textStyle="smallMedium" color="brand.darkPurple">
          Nominate, choose a winner, announce and pay
        </Text>
        <FieldHelp subject="a campaign" help={OVERVIEW} testId="campaign-overview-help" />
      </Flex>

      <Section
        title="Basics"
        help="What this award is called, and what employees are told it is for."
        testId="basics-help"
      >
        <FormControl isInvalid={!!errorFor('name')}>
          <LabelWithHelp
            label="Name"
            help="Employees see this name on the award and in every message about it."
            testId="name-help"
          />
          <Input
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            placeholder="Employee of the Month"
            data-testid="campaign-name-input"
          />
          <FormErrorMessage>{errorFor('name')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errorFor('description')}>
          <LabelWithHelp
            label="Description"
            help="A line saying what the award is for, so people know who to put forward."
            testId="description-help"
          />
          <Textarea
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
            placeholder="Awarded each month to someone who went above and beyond for a colleague."
            data-testid="campaign-description-input"
          />
          <FormErrorMessage>{errorFor('description')}</FormErrorMessage>
        </FormControl>
      </Section>

      <Section
        title="Where it runs"
        help="An award can have one winner for the whole company, or run separately inside each pride and pick a winner in every one."
        testId="where-it-runs-help"
      >
        <RadioGroup
          value={form.scope === 'GROUP' ? ONE_PRIDE : form.groupSelection}
          onChange={(value) => {
            if (value === ONE_PRIDE) {
              setField('scope', 'GROUP');
              return;
            }
            setField('scope', 'COMPANY');
            setField('groupSelection', value as GroupSelection);
          }}
        >
          <Stack gap="10px">
            {FAN_OUT_OPTIONS.map((option) => (
              <Flex key={option.value} align="center" gap="6px">
                <Radio value={option.value} data-testid={`fan-out-${option.value}`}>
                  {option.label}
                </Radio>
                <FieldHelp
                  subject={option.label}
                  help={option.help}
                  testId={`fan-out-${option.value}-help`}
                />
              </Flex>
            ))}
          </Stack>
        </RadioGroup>

        {form.scope === 'GROUP' && (
          <FormControl isInvalid={!!errorFor('groupIds')}>
            <LabelWithHelp
              label="Pride"
              help="The pride this award belongs to. Only its own people take part."
              testId="one-pride-help"
            />
            <Select
              placeholder="Choose a pride"
              value={form.groupId}
              onChange={(event) => setField('groupId', event.target.value)}
              data-testid="one-pride-select"
            >
              {prides.map((pride) => (
                <option key={pride.id} value={pride.id}>
                  {pride.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errorFor('groupIds')}</FormErrorMessage>
          </FormControl>
        )}

        {form.scope === 'COMPANY' && form.groupSelection === 'SELECTED' && (
          <FormControl isInvalid={!!errorFor('groupIds')}>
            <LabelWithHelp
              label="Prides"
              help="Tick every pride that should run this award. Each one gets its own winner."
              testId="prides-help"
            />
            <Stack gap="8px" maxH="220px" overflowY="auto" data-testid="pride-picker">
              {prides.map((pride) => (
                <Checkbox
                  key={pride.id}
                  isChecked={form.groupIds.includes(pride.id)}
                  onChange={(event) =>
                    setField(
                      'groupIds',
                      event.target.checked
                        ? [...form.groupIds, pride.id]
                        : form.groupIds.filter((id) => id !== pride.id)
                    )
                  }
                >
                  {pride.name}
                </Checkbox>
              ))}
            </Stack>
            <FormErrorMessage>{errorFor('groupIds')}</FormErrorMessage>
          </FormControl>
        )}
      </Section>

      <Section
        title="Who takes part"
        help="Who is allowed to nominate, who is allowed to be nominated, and the rules a nomination has to follow."
        testId="who-takes-part-help"
      >
        <FormControl isInvalid={!!errorFor('nominatorEligibility')}>
          <LabelWithHelp
            label="Who may nominate"
            help={eligibilityHelp('an employee')}
            testId="nominator-help"
          />
          <Select
            value={form.nominatorEligibility}
            onChange={(event) =>
              setField(
                'nominatorEligibility',
                event.target.value as CreateCampaignFormState['nominatorEligibility']
              )
            }
            data-testid="nominator-eligibility-select"
          >
            <option value="ALL_COMPANY">Anyone in the company</option>
            <option value="ALL_GROUP" disabled={!fansOut}>
              Only people in that pride
            </option>
            <option value="SELECTED_GROUPS">Only people in the prides you choose</option>
          </Select>
          <FormErrorMessage>{errorFor('nominatorEligibility')}</FormErrorMessage>
        </FormControl>

        {form.nominatorEligibility === 'SELECTED_GROUPS' && (
          <FormControl isInvalid={!!errorFor('voterGroupIds')}>
            <LabelWithHelp
              label="Prides who may nominate"
              help="Anyone belonging to one of these prides may nominate, whichever pride the person they put forward is in."
              testId="voter-groups-help"
            />
            <Stack gap="8px" maxH="200px" overflowY="auto" data-testid="voter-group-picker">
              {prides.map((pride) => (
                <Checkbox
                  key={pride.id}
                  isChecked={form.voterGroupIds.includes(pride.id)}
                  onChange={(event) =>
                    setField(
                      'voterGroupIds',
                      event.target.checked
                        ? [...form.voterGroupIds, pride.id]
                        : form.voterGroupIds.filter((id) => id !== pride.id)
                    )
                  }
                  data-testid={`voter-group-${pride.id}`}
                >
                  {pride.name}
                </Checkbox>
              ))}
            </Stack>
            <FormErrorMessage>{errorFor('voterGroupIds')}</FormErrorMessage>
          </FormControl>
        )}
        <FormControl isInvalid={!!errorFor('nomineeEligibility')}>
          <LabelWithHelp
            label="Who may be nominated"
            help={eligibilityHelp('anyone')}
            testId="nominee-help"
          />
          <Select
            value={form.nomineeEligibility}
            onChange={(event) =>
              setField(
                'nomineeEligibility',
                event.target.value as CreateCampaignFormState['nomineeEligibility']
              )
            }
            data-testid="nominee-eligibility-select"
          >
            <option value="ALL_COMPANY">Anyone in the company</option>
            <option value="ALL_GROUP" disabled={!fansOut}>
              Only people in that pride
            </option>
            <option value="SELECTED">Only a shortlist you choose</option>
          </Select>
          <FormErrorMessage>{errorFor('nomineeEligibility')}</FormErrorMessage>
        </FormControl>

        {form.nomineeEligibility === 'SELECTED' && (
          <FormControl isInvalid={!!errorFor('nomineeIndividualIds')}>
            <LabelWithHelp
              label="Who may be put forward"
              help="Nobody outside this shortlist can be nominated, whichever pride they are in."
              testId="nominee-shortlist-help"
            />
            <SearchDropdownInput
              selected={form.nomineeIndividualIds}
              options={candidates}
              placeholder="Search employees by name or email"
              itemName="person"
              onSearch={onSearchCandidates}
              onChange={(selected) => setField('nomineeIndividualIds', selected)}
              data-testid="nominee-shortlist-picker"
            />
            <FormErrorMessage>{errorFor('nomineeIndividualIds')}</FormErrorMessage>
          </FormControl>
        )}
        <FormControl isInvalid={!!errorFor('nominationsPerPerson')}>
          <LabelWithHelp
            label="Nominations per person, per run"
            help="How many colleagues one employee can put forward in a single run of the campaign. Counted across every pride, not once per pride."
            testId="nominations-per-person-help"
          />
          <Input
            type="number"
            min={1}
            value={form.nominationsPerPerson}
            onChange={(event) => setField('nominationsPerPerson', event.target.value)}
            data-testid="nominations-per-person-input"
          />
          <FormErrorMessage>{errorFor('nominationsPerPerson')}</FormErrorMessage>
        </FormControl>
        <CheckboxField
          isChecked={form.valuesRequired}
          onChange={(checked) => setField('valuesRequired', checked)}
          testId="values-required-checkbox"
          helpTestId="values-required-help"
          label="A company value is required on every nomination"
          help="Whoever nominates has to pick the company value the person showed. Turn this off to accept a nomination without one."
        />
        <CheckboxField
          isChecked={form.allowWinnerWithoutNominations}
          onChange={(checked) => setField('allowWinnerWithoutNominations', checked)}
          testId="allow-winner-without-nominations-checkbox"
          helpTestId="allow-winner-without-nominations-help"
          label="A winner may be picked who received no nominations"
          help="Lets whoever runs the campaign award anyone, even a person nobody put forward. Leave it off to keep the winner among those nominated."
        />

        {form.allowWinnerWithoutNominations && (
          <FormControl isInvalid={!!errorFor('winnerEligibility')}>
            <LabelWithHelp
              label="Who may win"
              help="Only read where a winner may be picked without nominations. Anyone nominated can win whatever this says."
              testId="winner-eligibility-help"
            />
            <Select
              value={form.winnerEligibility}
              onChange={(event) =>
                setField(
                  'winnerEligibility',
                  event.target.value as CreateCampaignFormState['winnerEligibility']
                )
              }
              data-testid="winner-eligibility-select"
            >
              <option value="ALL_COMPANY">Anyone in the company</option>
              <option value="ALL_GROUP" disabled={!fansOut}>
                Only people in that pride
              </option>
            </Select>
            <FormErrorMessage>{errorFor('winnerEligibility')}</FormErrorMessage>
          </FormControl>
        )}
      </Section>

      <Section
        title="Schedule"
        help={`The four moments one run of the campaign passes through, in order. The campaign is checked every ${SCHEDULE_STEP_MINUTES} minutes, which is why times run on the hour and the half hour.`}
        testId="schedule-help"
      >
        <Flex gap="16px" flexWrap="wrap">
          {SCHEDULE_MOMENTS.map((input) => (
            <FormControl
              key={input.key}
              flex="1"
              minW="240px"
              w="auto"
              isInvalid={!!errorFor(input.key)}
            >
              <LabelWithHelp
                label={input.label}
                help={input.help}
                testId={`${input.testId}-help`}
              />
              <Flex gap="8px">
                <Input
                  type="date"
                  flex="1"
                  value={dateOf(form[input.key])}
                  onChange={(event) =>
                    setField(input.key, join(event.target.value, timeOf(form[input.key])))
                  }
                  data-testid={input.testId}
                />
                <Select
                  w="110px"
                  placeholder="Time"
                  value={timeOf(form[input.key])}
                  onChange={(event) =>
                    setField(input.key, join(dateOf(form[input.key]), event.target.value))
                  }
                  data-testid={`${input.testId}-time`}
                >
                  {TIME_OPTIONS.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </Select>
              </Flex>
              <FormErrorMessage data-testid={`${input.testId}-error`}>
                {errorFor(input.key)}
              </FormErrorMessage>
            </FormControl>
          ))}
        </Flex>

        <FormControl>
          <LabelWithHelp
            label="Timezone"
            help="The zone every one of those moments is read in. Every campaign runs to UK time, so there is nothing to choose."
            testId="timezone-help"
          />
          <Input value={form.timezone} isReadOnly data-testid="timezone-input" />
        </FormControl>
      </Section>

      <Section
        title="Repeats"
        help="Whether this award runs again after this one, and how often. A successor is created as soon as the current run opens, so a recurring award keeps going without anyone setting it up each time."
        testId="repeats-help"
      >
        <RecurrenceFields
          form={form.recurrence}
          setField={(key, value) =>
            setField('recurrence', { ...form.recurrence, [key]: value } as RecurrenceFormState)
          }
          error={errorFor('recurrence')}
          forecast={forecast}
        />
      </Section>

      <Section
        title="Award"
        help="What the winner receives, and the pot those points are paid from."
        testId="award-help"
      >
        <FormControl isInvalid={!!errorFor('awardPoints')}>
          <LabelWithHelp
            label="Award in points"
            help="The points the winner is paid on the announcement date. Leave it empty for an award that is recognition only."
            testId="award-points-help"
          />
          <Input
            type="number"
            min={0}
            value={form.awardPoints}
            onChange={(event) => setField('awardPoints', event.target.value)}
            placeholder="Leave empty for an award with no points"
            data-testid="award-points-input"
          />
          {awardInPounds !== undefined && (
            <FormHelperText data-testid="award-points-value">
              Worth {formatCurrency(awardInPounds, 'GBP')} to the winner.
            </FormHelperText>
          )}
          <FormErrorMessage>{errorFor('awardPoints')}</FormErrorMessage>
        </FormControl>
        <ReachSummary
          prideCount={prideCount}
          awardPoints={form.awardPoints === '' ? undefined : Number(form.awardPoints)}
          resolvedEachRun={form.scope === 'COMPANY' && form.groupSelection === 'ALL'}
        />
        <Text textStyle="extraSmallRegular" color="brand.darkGray" data-testid="funding-pot-note">
          {form.awardPoints === ''
            ? 'An award with no points needs no funding pot, so none is created.'
            : "A funding pot is created for this campaign. The winner's points come out of it, so it has to hold enough before the announcement date."}
        </Text>
      </Section>

      <Section
        title="Advanced"
        help="Settings a campaign runs perfectly well without. Each one left blank takes the platform's own default."
        testId="advanced-help"
      >
        <AdvancedFields
          form={form.advanced}
          setField={(key, value) =>
            setField('advanced', { ...form.advanced, [key]: value } as AdvancedFormState)
          }
        />
        {errorFor('advanced') && (
          <Text textStyle="extraSmallRegular" color="text.error" data-testid="advanced-error">
            {errorFor('advanced')}
          </Text>
        )}
      </Section>

      <Flex gap="12px">
        <Button
          onClick={onSubmit}
          isDisabled={isSaving}
          isLoading={isSaving}
          data-testid="create-campaign-submit"
        >
          Create campaign
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="create-campaign-cancel">
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
}

export default CreateCampaignForm;
